import React from "react";
import { TextField } from "components";
import { usePropertyContext } from "../../context";
import { useExemptionsContext } from "../../ExemptionsContext";
import { useEventTracker } from "utils/useEventTracker";
import constants from "utils/constants";
import ButtonV2 from "elements/ButtonV2";

const SsnTinShared = ({
	variant,
}: {
	variant: "first-entry" | "second-entry";
}) => {
	const { details } = usePropertyContext();
	const {
		flowId,
		next,
		owner1SsnTin,
		setOwnerSsnTin,
		owner2SsnTin,
		setOwner2SsnTin,
		additionalOwnerFirstNameValue,
		setAdditionalOwnerFirstNameValue,
		additionalOwnerLastNameValue,
		setAdditionalOwnerLastNameValue,
	} = useExemptionsContext();
	const trackEvent = useEventTracker();

	const ssnTinLength = React.useMemo(() => {
		if (flowId === "MICHIGAN_EXEMPTION") {
			return 4;
		}

		return 9;
	}, [flowId]);

	const handleChange: React.ChangeEventHandler<HTMLInputElement> = event => {
		const { value } = event.target;
		// Strip '*' and non-digits
		const sanitizedValue = value.replace(/[^\d]/g, "").slice(0, ssnTinLength);

		if (variant === "first-entry") {
			setOwnerSsnTin(sanitizedValue);

			// Commenting out this code because I'm less confident about it.
			// if (sanitizedValue.length === 1) {
			// 	// append value
			// 	setOwnerSsnTin((owner1SsnTin || "").concat(sanitizedValue));
			// } else {
			// 	setOwnerSsnTin((owner1SsnTin || "").slice(0, -1));
			// }
			// .... value={"*".repeat((value || "").length)}
		}

		if (variant === "second-entry") {
			setOwner2SsnTin(sanitizedValue);
		}
	};

	const handleSubmit = async () => {
		if (variant === "first-entry") {
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_SSN_TIN_Answer,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
				},
			});
		}

		if (variant === "second-entry") {
			trackEvent({
				eventName: constants.EVENTS.Exemptions_Flow_SSN_TIN2_Answer,
				data: {
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
				},
			});
		}
		const trackingEventData = {
			variant: variant,
		};

		next({ trackingEventData });
	};

	const value = React.useMemo(() => {
		if (variant === "first-entry") {
			return owner1SsnTin;
		}

		if (variant === "second-entry") {
			return owner2SsnTin;
		}
	}, [owner1SsnTin, owner2SsnTin]);

	const buttonDisabled = React.useMemo(() => {
		if (variant === "first-entry") {
			return owner1SsnTin?.length !== ssnTinLength;
		}
		if (variant === "second-entry") {
			return owner2SsnTin?.length !== ssnTinLength;
		}
	}, [owner1SsnTin, owner2SsnTin, ssnTinLength]);

	const title = React.useMemo(() => {
		if (flowId === "CALIFORNIA_EXEMPTION") {
			if (variant === "first-entry") {
				return "What is your Social Security or Taxpayer Identification Number?";
			}
			if (variant === "second-entry") {
				return "Additional Owner Details";
			}
		}

		if (flowId === "GEORGIA_EXEMPTION") {
			if (variant === "first-entry") {
				return "What is your Social Security Number?";
			}
			if (variant === "second-entry") {
				return `What is ${additionalOwnerFirstNameValue} ${additionalOwnerLastNameValue}'s Social Security Number?`;
			}
		}

		if (flowId === "NEW_YORK_EXEMPTION") {
			if (variant === "first-entry") {
				return "What is your Social Security Number?";
			}
			if (variant === "second-entry") {
				return `What is ${additionalOwnerFirstNameValue} ${additionalOwnerLastNameValue}'s Social Security Number?`;
			}
		}

		if (flowId === "FLORIDA_EXEMPTION") {
			if (variant === "first-entry") {
				return "What is your Social Security Number?";
			}
			if (variant === "second-entry") {
				return `What is ${additionalOwnerFirstNameValue} ${additionalOwnerLastNameValue}'s Social Security Number?`;
			}
		}

		if (flowId === "MICHIGAN_EXEMPTION") {
			if (variant === "first-entry") {
				return "What are the last 4 digits of your Social Security Number?";
			}
			if (variant === "second-entry") {
				return `What are the last 4 digits of ${additionalOwnerFirstNameValue} ${additionalOwnerLastNameValue}'s Social Security Number?`;
			}
		}
	}, [flowId, variant]);

	const subtitle = React.useMemo(() => {
		if (flowId === "CALIFORNIA_EXEMPTION") {
			return "The state requires a social security number or taxpayer identification number from any property owners claiming residency in order to file.";
		}

		if (flowId === "GEORGIA_EXEMPTION") {
			return "The state requires a social security number from any property owners claiming residency in order to file. If they do not have a social security number, please provide a green card number.";
		}

		if (flowId === "NEW_YORK_EXEMPTION") {
			return "The state requires a social security number from any property owners claiming residency in order to file.";
		}

		if (flowId === "FLORIDA_EXEMPTION") {
			return "The state requires a social security number from any property owners claiming residency in order to file.";
		}

		if (flowId === "MICHIGAN_EXEMPTION") {
			return "The state requires a social security number from any property owners claiming residency in order to file.";
		}

		return "";
	}, [flowId]);

	const inputLabel = React.useMemo(() => {
		if (flowId === "CALIFORNIA_EXEMPTION") {
			return "Social Security or Taxpayer Identification Number";
		}

		if (flowId === "GEORGIA_EXEMPTION") {
			return "Social Security";
		}

		if (flowId === "MICHIGAN_EXEMPTION") {
			return "Last 4 of Social Security Number";
		}

		if (flowId === "NEW_YORK_EXEMPTION") {
			return "Social Security";
		}

		if (flowId === "FLORIDA_EXEMPTION") {
			return "Social Security";
		}

		return "";
	}, [flowId]);

	return (
		<form
			onSubmit={e => {
				e.preventDefault();
				handleSubmit();
			}}>
			<div className="property-profile-question-card exemption-survey-card">
				<div className="property-profile-question-card-title-container">
					<p>{title}</p>
				</div>
				<p className="exemption-question-card-subtitle sm">{subtitle}</p>
				{flowId === "CALIFORNIA_EXEMPTION" && variant === "second-entry" && (
					<div className="exemptions-input-flex-container">
						<TextField
							required
							label="First Name"
							placeholder="First Name"
							containerClassName="exemptions-card-input"
							value={additionalOwnerFirstNameValue ?? ""}
							onChange={e => {
								setAdditionalOwnerFirstNameValue(e.target.value);
							}}
						/>
						<TextField
							required
							label="Last Name"
							placeholder="Last Name"
							containerClassName="exemptions-card-input"
							value={additionalOwnerLastNameValue ?? ""}
							onChange={e => {
								setAdditionalOwnerLastNameValue(e.target.value);
							}}
						/>
					</div>
				)}

				<TextField
					required
					label={inputLabel}
					placeholder={Array(ssnTinLength).fill("*").join("")}
					containerClassName="exemptions-card-input"
					maxLength={ssnTinLength}
					minLength={ssnTinLength}
					numeric
					autoComplete="off"
					value={value ?? ""}
					onChange={handleChange}
				/>
			</div>
			<ButtonV2 size="large" disabled={buttonDisabled} type="submit">
				Continue
			</ButtonV2>
		</form>
	);
};

export const SsnTin = () => <SsnTinShared variant="first-entry" />;
export const SsnTin2 = () => <SsnTinShared variant="second-entry" />;
